import React, { useEffect, useState } from 'react';
import { FileText, Edit, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const AdminBlog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchArticles = () => {
    axios.get(`${API}/blog?published_only=false`)
      .then(res => setArticles(res.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.keywords = data.keywords.split(',').map(k => k.trim()).filter(k => k);
    data.published = data.published === 'on';

    try {
      if (editingArticle) {
        await axios.put(`${API}/blog/${editingArticle.slug}`, data, { headers });
        toast.success('Artikel aktualisiert!');
      } else {
        await axios.post(`${API}/blog`, data, { headers });
        toast.success('Artikel erstellt!');
      }
      setIsDialogOpen(false);
      setEditingArticle(null);
      fetchArticles();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Möchtest du diesen Artikel wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/blog/${slug}`, { headers });
      toast.success('Artikel gelöscht!');
      fetchArticles();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const togglePublished = async (article) => {
    try {
      await axios.put(`${API}/blog/${article.slug}`, { published: !article.published }, { headers });
      toast.success(article.published ? 'Artikel versteckt' : 'Artikel veröffentlicht');
      fetchArticles();
    } catch (error) {
      toast.error('Fehler');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog-Artikel</h1>
          <p className="text-slate-600">Verwalte deine Ratgeber und Blog-Beiträge.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingArticle(null)} data-testid="add-article-btn">
              <Plus className="w-4 h-4 mr-2" /> Neuer Artikel
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingArticle ? 'Artikel bearbeiten' : 'Neuer Artikel'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Titel</Label>
                  <Input id="title" name="title" defaultValue={editingArticle?.title} required />
                </div>
                <div>
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <Input id="slug" name="slug" defaultValue={editingArticle?.slug} required />
                </div>
              </div>
              <div>
                <Label htmlFor="excerpt">Kurzbeschreibung</Label>
                <Textarea id="excerpt" name="excerpt" rows={2} defaultValue={editingArticle?.excerpt} required />
              </div>
              <div>
                <Label htmlFor="content">Inhalt (Markdown)</Label>
                <Textarea id="content" name="content" rows={12} defaultValue={editingArticle?.content} required />
              </div>
              <div>
                <Label htmlFor="image_url">Bild URL</Label>
                <Input id="image_url" name="image_url" defaultValue={editingArticle?.image_url} />
              </div>
              <div>
                <Label htmlFor="keywords">Keywords (kommagetrennt)</Label>
                <Input id="keywords" name="keywords" defaultValue={editingArticle?.keywords?.join(', ')} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input id="meta_title" name="meta_title" defaultValue={editingArticle?.meta_title} />
                </div>
                <div>
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Input id="meta_description" name="meta_description" defaultValue={editingArticle?.meta_description} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="published" 
                  name="published" 
                  defaultChecked={editingArticle?.published ?? true}
                  className="rounded"
                />
                <Label htmlFor="published">Veröffentlicht</Label>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">Speichern</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {articles.map((article) => (
          <Card key={article.slug} data-testid={`article-item-${article.slug}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {article.image_url && (
                  <img 
                    src={article.image_url} 
                    alt={article.title}
                    className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{article.title}</h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-sm text-slate-500">
                          {new Date(article.created_at).toLocaleDateString('de-DE')}
                        </span>
                        <span className={`badge ${article.published ? 'badge-success' : 'badge-warning'}`}>
                          {article.published ? 'Veröffentlicht' : 'Entwurf'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => togglePublished(article)}
                        title={article.published ? 'Verstecken' : 'Veröffentlichen'}
                      >
                        {article.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setEditingArticle(article); setIsDialogOpen(true); }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(article.slug)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {articles.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Noch keine Artikel vorhanden.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlog;
